import { BASE_RECOVERY_TOKEN_ARGUMENTS } from "./recoveryAbi";
import { ethers } from "ethers";
import { ACTIVE_EVM_NETWORK_KEY, EVM_CONFIG } from "../config";
import {
  decodePrivateBridge,
  privateBridgeDeployment,
  privateCcipBridgeDeployment,
  bridgeDeploymentFor,
} from "./privateBridgeConfig";
import { decodePrivateSwap, privateSwapDeployment } from "./privateSwapConfig";
import type { StoredJoinSplitNote } from "../joinsplit/types";

export function privateTokenPool(): string | null {
  const value = process.env.REACT_APP_PRIVATE_TOKEN_POOL;
  // A Base Sepolia deployment: the DogeOS page of the same build has none.
  if (!value || ACTIVE_EVM_NETWORK_KEY === "dogeos-chikyu") return null;
  if (
    EVM_CONFIG.CHAIN_ID !== 84532 ||
    !ethers.isAddress(value) ||
    !ethers.isAddress(process.env.REACT_APP_PRIVATE_TOKEN_RELAY || "") ||
    !Number.isSafeInteger(
      Number(process.env.REACT_APP_PRIVATE_TOKEN_DEPLOYMENT_BLOCK),
    ) ||
    Number(process.env.REACT_APP_PRIVATE_TOKEN_DEPLOYMENT_BLOCK) <= 0
  )
    throw Error("Token deployment is not configured");
  return ethers.getAddress(value);
}
export function privatePoolForToken(token: string): string {
  return ethers.getAddress(token) === ethers.ZeroAddress
    ? EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT
    : privateTokenPool() || EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT;
}
export function privatePools(): string[] {
  return [
    ...new Set(
      [
        EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
        privateTokenPool(),
        privateSwapDeployment()?.pool,
      ]
        .filter((p): p is string => !!p)
        .map((p) => ethers.getAddress(p)),
    ),
  ];
}
/** One proof spends one pool. Prefer a pool that covers this amount; MAX uses
 * its spendable subtotal so an existing balance is never hidden or stranded. */
export function privateSpendSource(
  notes: readonly StoredJoinSplitNote[],
  token: string,
  amount = 0n,
) {
  const unique = new Map<string, StoredJoinSplitNote>();
  for (const item of notes) {
    const n = item.note;
    if (
      item.spent ||
      n.protocol !== "flat-joinsplit-v2" ||
      n.schemaVersion !== 2 ||
      n.chainId !== EVM_CONFIG.CHAIN_ID ||
      !Number.isSafeInteger(n.treeIndex) ||
      !Number.isSafeInteger(n.leafIndex) ||
      n.leafIndex < 0 ||
      n.value <= 0n ||
      !ethers.isHexString(n.commitment, 32)
    )
      continue;
    unique.set(
      `${n.poolAddress?.toLowerCase()}:${n.commitment.toLowerCase()}`,
      item,
    );
  }
  const groups = privatePools()
    .map((pool) => ({
      pool,
      amount: [...unique.values()]
        .filter(
          (item) =>
            item.note.poolAddress?.toLowerCase() === pool.toLowerCase() &&
            item.note.tokenData?.tokenAddress?.toLowerCase() ===
              token.toLowerCase(),
        )
        .reduce((n, item) => n + BigInt(item.note.value), 0n),
    }))
    .sort((a, b) => (a.amount > b.amount ? -1 : a.amount < b.amount ? 1 : 0));
  return (
    groups.find((g) => g.amount >= amount && g.amount > 0n) ||
    groups[0] || { pool: privatePoolForToken(token), amount: 0n }
  );
}
export function privatePoolDeploymentBlock(pool: string): number {
  if (pool.toLowerCase() === privateSwapDeployment()?.pool.toLowerCase())
    return privateSwapDeployment()!.block;
  return pool.toLowerCase() === privateTokenPool()?.toLowerCase()
    ? Number(process.env.REACT_APP_PRIVATE_TOKEN_DEPLOYMENT_BLOCK)
    : EVM_CONFIG.JOINSPLIT_V2.DEPLOYMENT_BLOCK;
}
export const PRIVATE_TOKEN_RELAY_INTERFACE = new ethers.Interface([
  `function settle(((bytes32 merkleRoot,bytes32[] nullifiers,bytes32[] commitments,address token,address recipient,uint256 publicAmount,bytes proof) settlement,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes feeProof,bytes signature) tokenLeg,((bytes32 merkleRoot,bytes32[] nullifiers,bytes32[] commitments,address token,address recipient,uint256 publicAmount,bytes proof) settlement,(bytes32 intentHash,bytes32 termsHash,bytes32 nonce,uint64 issuedAt,uint64 deadline,uint8 kind) quote,bytes feeProof,bytes signature) gasLeg${BASE_RECOVERY_TOKEN_ARGUMENTS})`,
]);

/** Only the pinned, noncustodial two-pool relay may be a journal target. */
export function isPrivateSettlementTarget(
  to: string,
  pool: string,
  data: string,
): boolean {
  if (ethers.getAddress(to) === ethers.getAddress(pool)) return true;
  if (
    [privateBridgeDeployment(), privateCcipBridgeDeployment()].some(
      (d) => to.toLowerCase() === d?.coordinator.toLowerCase(),
    )
  ) {
    const decoded = decodePrivateBridge(data);
    return (
      !!decoded &&
      to.toLowerCase() ===
        bridgeDeploymentFor(decoded.provider)?.coordinator.toLowerCase() &&
      decoded.route.sourcePool.toLowerCase() === pool.toLowerCase() &&
      privatePools().some((p) => p.toLowerCase() === pool.toLowerCase())
    );
  }
  if (to.toLowerCase() === privateSwapDeployment()?.coordinator.toLowerCase()) {
    const decoded = decodePrivateSwap(data);
    return (
      !!decoded &&
      decoded.route.sourcePool.toLowerCase() === pool.toLowerCase() &&
      privatePools().some((p) => p.toLowerCase() === pool.toLowerCase())
    );
  }
  if (
    !privateTokenPool() ||
    to.toLowerCase() !==
      process.env.REACT_APP_PRIVATE_TOKEN_RELAY?.toLowerCase() ||
    !privatePools().some((p) => p.toLowerCase() === pool.toLowerCase())
  )
    return false;
  try {
    const call = PRIVATE_TOKEN_RELAY_INTERFACE.decodeFunctionData(
      "settle",
      data,
    );
    return (
      call.tokenLeg.settlement.token !== ethers.ZeroAddress &&
      call.gasLeg.settlement.token === ethers.ZeroAddress &&
      Number(call.gasLeg.quote.kind) === 4 &&
      call.gasLeg.settlement.publicAmount === 0n &&
      call.gasLeg.settlement.recipient === ethers.ZeroAddress
    );
  } catch {
    return false;
  }
}
